import {
  TargetSpecType,
  FieldMapping,
  MappingResults,
  MappingConfidence,
  LLMSettings,
  UnmappedSourceFieldEntry,
} from '../types';
import { getHpsSpec } from './validationService';
import { getTokens } from '../authentication/auth';

// ─── Source file parsing ────────────────────────────────────────────

interface ParsedSourceData {
  format: string;
  fields: {
    name: string;
    example: string;
    examples?: string[];
    dataType?: string;
    valueHint?: string;
    sourceFeatures?: SafeSourceFeatures;
  }[];
  rows: Record<string, string>[];
}

type SourceField = ParsedSourceData['fields'][number];

interface SafeSourceFeatures {
  valueHint: string;
  inferredDataType: string;
  lengthBucket: string;
  blankRate: number;
  cardinalityHint: string;
  patternClasses: string[];
  parentPath: string;
}

interface ParseOptions {
  maxSampleRows?: number;
}

const MAX_EXAMPLES_PER_FIELD = 3;
const DEFAULT_MAX_SAMPLE_ROWS = 100;
const WRAPPING_DOUBLE_QUOTE_PATTERN = /(?:^"|"$)/g;
const XML_PATH_BOUNDARY_DOTS_PATTERN = /(?:^\.+|\.+$)/g;

function getSampleLimit(options?: ParseOptions): number {
  const raw = Number(options?.maxSampleRows ?? DEFAULT_MAX_SAMPLE_ROWS);
  return Number.isFinite(raw) ? Math.min(Math.max(Math.round(raw), 1), 500) : DEFAULT_MAX_SAMPLE_ROWS;
}

function collectDistinctExamples(rows: Record<string, string>[], fieldName: string, limit = 3): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const row of rows) {
    const val = (row[fieldName] ?? '').trim();
    if (val && !seen.has(val)) {
      seen.add(val);
      result.push(val);
      if (result.length >= limit) break;
    }
  }
  return result;
}

function rememberExample(examplesByField: Map<string, string[]>, fieldName: string, value: string): void {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return;
  const examples = examplesByField.get(fieldName) ?? [];
  if (examples.length >= MAX_EXAMPLES_PER_FIELD || examples.includes(trimmed)) return;
  examples.push(trimmed);
  examplesByField.set(fieldName, examples);
}

function splitDelimitedLine(line: string, delimiter: string): string[] {
  const cols: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === delimiter && !inQuotes) {
      cols.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cols.push(current.trim());
  return cols;
}

function detectFormat(content: string, fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const trimmed = content.trim();
  const isLikelyEdi =
    ext === 'edi' ||
    ext === 'x12' ||
    /^ISA\*/i.test(trimmed) ||
    (/~/.test(content) && /\b(?:ISA|GS|ST|BHT|HL|NM1|DTP|SE|GE|IEA)\*/i.test(content));

  if (isLikelyEdi) return 'EDI';
  if (ext === 'json' || content.trim().startsWith('{') || content.trim().startsWith('[')) return 'JSON';
  if (ext === 'csv' || (content.includes(',') && content.split('\n')[0].split(',').length > 2)) return 'CSV';
  if (ext === 'tsv' || content.includes('\t')) return 'TSV';
  if (ext === 'xml' || content.trim().startsWith('<')) return 'XML';
  if (content.includes('|')) return 'Pipe-Delimited';
  return 'Fixed-Width/Text';
}

function scalarToString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function flattenJsonValue(value: unknown, prefix: string, out: Record<string, string>): void {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      if (prefix) out[prefix] = '';
      return;
    }
    if (value.every(item => item === null || typeof item !== 'object')) {
      out[prefix || 'value'] = value.map(scalarToString).join('; ');
      return;
    }
    value.slice(0, 3).forEach((item, idx) => {
      flattenJsonValue(item, `${prefix}[${idx}]`, out);
    });
    return;
  }

  if (value && typeof value === 'object') {
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      const path = prefix ? `${prefix}.${key}` : key;
      flattenJsonValue(child, path, out);
    }
    return;
  }

  out[prefix || 'value'] = scalarToString(value);
}

function parseJSON(content: string, options?: ParseOptions): ParsedSourceData {
  const parsed = JSON.parse(content);
  const arr: unknown[] = Array.isArray(parsed) ? parsed : [parsed];
  const fieldNames = new Set<string>();
  const flattenedRows = arr.map(obj => {
    const flattened: Record<string, string> = {};
    flattenJsonValue(obj, '', flattened);
    Object.keys(flattened).forEach(k => fieldNames.add(k));
    return flattened;
  });
  const rows = flattenedRows.slice(0, getSampleLimit(options)).map(obj => {
    const row: Record<string, string> = {};
    fieldNames.forEach(k => { row[k] = obj[k] ?? ''; });
    return row;
  });
  const fields = Array.from(fieldNames).map(name => {
    const examples = collectDistinctExamples(flattenedRows, name);
    return {
      name,
      example: examples[0] ?? '',
      examples,
    };
  });
  return { format: 'JSON', fields, rows };
}

function parseCSV(content: string, delimiter = ',', options?: ParseOptions): ParsedSourceData {
  const lines = content.split(/\r?\n/).filter(l => l.trim());
  if (lines.length === 0) return { format: 'CSV', fields: [], rows: [] };
  const sampleLimit = getSampleLimit(options);
  const headers = splitDelimitedLine(lines[0], delimiter).map(h => h.trim().replace(WRAPPING_DOUBLE_QUOTE_PATTERN, ''));
  const rows: Record<string, string>[] = [];
  const examplesByField = new Map<string, string[]>();
  for (let i = 1; i < lines.length; i++) {
    const cols = splitDelimitedLine(lines[i], delimiter).map(c => c.trim().replace(WRAPPING_DOUBLE_QUOTE_PATTERN, ''));
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      const value = cols[idx] ?? '';
      row[h] = value;
      rememberExample(examplesByField, h, value);
    });
    if (rows.length < sampleLimit) rows.push(row);
  }
  const fields = headers.map(name => {
    const examples = examplesByField.get(name) ?? [];
    return {
      name,
      example: examples[0] ?? '',
      examples,
    };
  });
  return { format: 'CSV', fields, rows };
}

function parseTSV(content: string, options?: ParseOptions): ParsedSourceData {
  const result = parseCSV(content, '\t', options);
  result.format = 'TSV';
  return result;
}

function parsePipeDelimited(content: string, targetSpecType?: TargetSpecType, options?: ParseOptions): ParsedSourceData {
  if (targetSpecType && looksLikeHpsDelimited(content, targetSpecType)) {
    return parseHpsPipeDelimited(content, targetSpecType, options);
  }

  const result = parseCSV(content, '|', options);
  result.format = 'Pipe-Delimited';
  return result;
}

function looksLikeHpsDelimited(content: string, targetSpecType?: TargetSpecType): boolean {
  const lines = content.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length === 0) return false;

  const firstToken = (line: string) => (line.split('|')[0] || '').trim().toUpperCase();

  if (targetSpecType) {
    const spec = getHpsSpec(targetSpecType);
    const validCodes = new Set(Object.keys(spec.records).map(k => k.toUpperCase()));
    let recognized = 0;
    for (const line of lines.slice(0, Math.min(lines.length, 10))) {
      if (validCodes.has(firstToken(line))) recognized++;
    }
    return recognized >= Math.min(2, lines.length);
  }

  const fallbackCodes = new Set(['HDR', 'CLM', 'DTL', 'STC', 'TRLR']);
  let recognized = 0;
  for (const line of lines.slice(0, Math.min(lines.length, 10))) {
    if (fallbackCodes.has(firstToken(line))) recognized++;
  }
  return recognized >= Math.min(2, lines.length);
}

function parseHpsPipeDelimited(content: string, targetSpecType: TargetSpecType, options?: ParseOptions): ParsedSourceData {
  const spec = getHpsSpec(targetSpecType);
  const lines = content.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const sampleLimit = getSampleLimit(options);

  const fieldsMap = new Map<string, { name: string; example: string; examples: string[]; dataType?: string }>();
  const rows: Record<string, string>[] = [];

  for (const line of lines) {
    const cols = line.split('|').map(c => c.trim());
    const recordType = (cols[0] || '').toUpperCase();
    const recordSpec = spec.records[recordType];
    if (!recordSpec) continue;

    const row: Record<string, string> = {};
    for (const field of recordSpec.fields) {
      const idx = (field.num ?? 0) - 1;
      const value = (cols[idx] ?? '').trim();
      const sourceFieldName = `${recordType}.${field.name}`;

      row[sourceFieldName] = value;

      const existing = fieldsMap.get(sourceFieldName);
      if (!existing) {
        fieldsMap.set(sourceFieldName, {
          name: sourceFieldName,
          example: value,
          examples: value ? [value] : [],
          dataType: field.dataType,
        });
      } else {
        if (!existing.example && value) {
          existing.example = value;
        }
        if (value && existing.examples.length < 3 && !existing.examples.includes(value)) {
          existing.examples.push(value);
        }
      }
    }

    if (rows.length < sampleLimit) rows.push(row);
  }

  return {
    format: 'Pipe-Delimited (HPS)',
    fields: Array.from(fieldsMap.values()),
    rows,
  };
}

function parseXML(content: string, options?: ParseOptions): ParsedSourceData {
  const rows: Record<string, string>[] = [];
  const fieldsMap = new Map<string, { name: string; example: string; examples: string[] }>();

  const rememberXmlField = (name: string, value: string) => {
    const cleanName = name.replace(XML_PATH_BOUNDARY_DOTS_PATTERN, '');
    if (!cleanName) return;
    const existing = fieldsMap.get(cleanName);
    if (!existing) {
      fieldsMap.set(cleanName, {
        name: cleanName,
        example: value,
        examples: value ? [value] : [],
      });
    } else if (value && existing.examples.length < MAX_EXAMPLES_PER_FIELD && !existing.examples.includes(value)) {
      existing.examples.push(value);
      if (!existing.example) existing.example = value;
    }
  };

  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(content, 'application/xml');
    const parserError = doc.querySelector('parsererror');
    if (!parserError && doc.documentElement) {
      const row: Record<string, string> = {};
      const visit = (node: Element, path: string) => {
        const children = Array.from(node.children);
        const nextPath = path ? `${path}.${node.tagName}` : node.tagName;
        if (children.length === 0) {
          const value = node.textContent?.trim() ?? '';
          row[nextPath] = value;
          rememberXmlField(nextPath, value);
          return;
        }
        children.forEach(child => visit(child, nextPath));
      };
      visit(doc.documentElement, '');
      rows.push(row);
      return { format: 'XML', fields: Array.from(fieldsMap.values()), rows };
    }
  }

  const tagPattern = /<([A-Za-z_][\w:.-]*)\b[^>]*>([^<]+)<\/\1>/g;
  let match;
  const row: Record<string, string> = {};
  while ((match = tagPattern.exec(content)) !== null && fieldsMap.size < getSampleLimit(options)) {
    const name = match[1].trim();
    const value = match[2].trim();
    row[name] = value;
    rememberXmlField(name, value);
  }
  if (fieldsMap.size > 0) {
    rows.push(row);
    return { format: 'XML', fields: Array.from(fieldsMap.values()), rows };
  }

  return parseGenericText(content, options);
}

function parseGenericText(content: string, options?: ParseOptions): ParsedSourceData {
  // For transcripts or free-form text, extract key-value-like patterns
  const kvPattern = /([A-Za-z_][A-Za-z0-9_ ]*)\s*[:=]\s*(.+)/g;
  const fields: { name: string; example: string }[] = [];
  const seen = new Set<string>();
  let match;
  while ((match = kvPattern.exec(content)) !== null) {
    const name = match[1].trim();
    if (!seen.has(name.toLowerCase())) {
      seen.add(name.toLowerCase());
      fields.push({ name, example: match[2].trim() });
    }
  }
  if (fields.length === 0) {
    // Fallback: treat each non-empty line as a field
    const lines = content.split(/\r?\n/).filter(l => l.trim());
    lines.slice(0, getSampleLimit(options)).forEach((line, i) => {
      fields.push({ name: `Field_${i + 1}`, example: line.trim().substring(0, 60) });
    });
  }
  return { format: 'Text', fields, rows: [] };
}

function parseFixedWidth(content: string, options?: ParseOptions): ParsedSourceData {
  const lines = content.split(/\r?\n/).filter(l => l.trim());
  if (lines.length === 0) return { format: 'Fixed-Width', fields: [], rows: [] };
  const sampleLimit = getSampleLimit(options);

  // Heuristic 1: table-like fixed width with 2+ spaces separating columns.
  const headerColumns = lines[0].split(/\s{2,}/).map(c => c.trim()).filter(Boolean);
  if (headerColumns.length > 1) {
    const rows: Record<string, string>[] = [];
    const examplesByField = new Map<string, string[]>();
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(/\s{2,}/).map(c => c.trim());
      const row: Record<string, string> = {};
      headerColumns.forEach((h, idx) => {
        const value = cols[idx] ?? '';
        row[h] = value;
        rememberExample(examplesByField, h, value);
      });
      if (rows.length < sampleLimit) rows.push(row);
    }
    const fields = headerColumns.map(name => ({
      name,
      example: examplesByField.get(name)?.[0] ?? '',
      examples: examplesByField.get(name) ?? [],
    }));
    return { format: 'Fixed-Width', fields, rows };
  }

  // Heuristic 2: strict fixed-width records with no obvious separators.
  // Slice each record into deterministic 10-char windows to preserve positional data.
  const sample = lines[0];
  const width = Math.min(sample.length, 120);
  const chunkSize = 10;
  const chunkCount = Math.max(1, Math.ceil(width / chunkSize));
  const fieldNames = Array.from({ length: chunkCount }, (_, i) => `Col_${String(i + 1).padStart(2, '0')}`);

  const rows: Record<string, string>[] = [];
  const examplesByField = new Map<string, string[]>();
  for (const line of lines) {
    const row: Record<string, string> = {};
    fieldNames.forEach((name, idx) => {
      const start = idx * chunkSize;
      const value = (line.slice(start, start + chunkSize) || '').trim();
      row[name] = value;
      rememberExample(examplesByField, name, value);
    });
    if (rows.length < sampleLimit) rows.push(row);
  }

  const fields = fieldNames.map(name => ({
    name,
    example: examplesByField.get(name)?.[0] ?? '',
    examples: examplesByField.get(name) ?? [],
  }));

  return { format: 'Fixed-Width', fields, rows };
}

function parseEdi(content: string, options?: ParseOptions): ParsedSourceData {
  const segments = content
    .split('~')
    .map(s => s.trim())
    .filter(Boolean);
  const sampleLimit = getSampleLimit(options);

  const rows: Record<string, string>[] = [];
  const fieldsMap = new Map<string, { name: string; example: string; examples: string[] }>();

  for (const segment of segments) {
    const parts = segment.split('*').map(p => p.trim());
    const segName = (parts[0] || 'SEG').toUpperCase();
    const row: Record<string, string> = { Segment: segName };

    for (let i = 1; i < parts.length; i++) {
      const fieldName = `${segName}.${String(i).padStart(2, '0')}`;
      const value = parts[i] ?? '';
      row[fieldName] = value;

      const existing = fieldsMap.get(fieldName);
      if (!existing) {
        fieldsMap.set(fieldName, { name: fieldName, example: value, examples: value ? [value] : [] });
      } else if (value && !existing.examples.includes(value) && existing.examples.length < 3) {
        existing.examples.push(value);
      }
    }

    if (rows.length < sampleLimit) rows.push(row);
  }

  return {
    format: 'EDI',
    fields: Array.from(fieldsMap.values()),
    rows,
  };
}

type ValueHintRule = { pattern: RegExp; hint: string };

const FIELD_NAME_VALUE_HINT_RULES: ValueHintRule[] = [
  { pattern: /\b(plan|group|network|file|record|field)\s*name\b/, hint: '' },
  { pattern: /\b(first|last|middle|maiden|person|patient|member|subscriber|insured|beneficiary).*\bname\b|\bname\b/, hint: 'PERSON_NAME' },
  { pattern: /\b(email|e-mail)\b/, hint: 'EMAIL' },
  { pattern: /\b(address|street)\b/, hint: 'ADDRESS' },
  { pattern: /\bcity\b/, hint: 'CITY' },
  { pattern: /\bcounty\b/, hint: 'COUNTY' },
  { pattern: /\b(zip|postal)\b/, hint: 'ZIP' },
  { pattern: /\b(dob|date of birth|birth date|birthdate)\b/, hint: 'DOB' },
  { pattern: /\b(phone|fax|telephone)\b/, hint: 'PHONE' },
  { pattern: /\b(ssn|social security|ein|tax id|tax identification)\b/, hint: 'SSN' },
  { pattern: /\b(npi|provider identifier)\b/, hint: 'NPI' },
  { pattern: /\b(gender|sex)\b/, hint: 'GENDER' },
  { pattern: /\b(member|subscriber|patient|claim|account|auth|authorization|tracking|case).*\b(id|number|identifier|no)\b/, hint: 'ID_NUMBER' },
];

const EXAMPLE_VALUE_HINT_RULES: ValueHintRule[] = [
  { pattern: /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])/, hint: 'DATE' },
  { pattern: /^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/, hint: 'DATE' },
  { pattern: /^(?:[01]\d|2[0-3])[0-5]\d[0-5]\d$/, hint: 'TIME' },
  { pattern: /^\$?\d+\.\d{2}$/, hint: 'CURRENCY' },
  { pattern: /^\d{3}-\d{2}-\d{4}$|^\d{2}-\d{7}$/, hint: 'SSN' },
  { pattern: /^(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]\d{3}[-.\s]\d{4}$/, hint: 'PHONE' },
  { pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, hint: 'EMAIL' },
  { pattern: /^\d{5}(-\d{4})?$/, hint: 'ZIP' },
  { pattern: /^\d{3}[A-Z]\d{5}[A-Z0-9]$/, hint: 'TAXONOMY' },
  { pattern: /^[A-Z]\d{4}[A-Z0-9]?$/, hint: 'PROCEDURE' },
  { pattern: /^[FMUfmu]$/, hint: 'GENDER' },
  { pattern: /^[YNyn]$/, hint: 'INDICATOR' },
  { pattern: /^[A-Z]{2}$/, hint: 'QUALIFIER' },
  { pattern: /^\d+(\.\d+)?$/, hint: 'NUM' },
  { pattern: /^\d{1,5}\s+.*\b(St|Street|Ave|Avenue|Blvd|Boulevard|Dr|Drive|Ln|Lane|Rd|Road|Ct|Court|Way|Place|Circle|Parkway|Terrace|Highway)\b/i, hint: 'ADDRESS' },
  { pattern: /^[A-Z][a-z]{1,20}(?:\s+[A-Z][a-z]{1,20}){1,3}$/, hint: 'PERSON_NAME' },
];

function firstMatchingHint(input: string, rules: ValueHintRule[]): string | undefined {
  return rules.find(({ pattern }) => pattern.test(input))?.hint;
}

function inferValueHint(value: string, fieldName = ''): string {
  const name = fieldName.toLowerCase().replace(/[_.-]/g, ' ');
  const nameHint = firstMatchingHint(name, FIELD_NAME_VALUE_HINT_RULES);
  if (nameHint !== undefined) return nameHint;

  const v = String(value ?? '').trim();
  if (!v) return '';

  return firstMatchingHint(v, EXAMPLE_VALUE_HINT_RULES) ?? 'TEXT';
}

function inferDataType(value: string): string {
  if (!value) return 'AN';
  if (/^\d{8}$/.test(value) && /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/.test(value)) return 'DT';
  if (/^\d{6}$/.test(value) && /^(?:[01]\d|2[0-3])[0-5]\d[0-5]\d$/.test(value)) return 'TM';
  if (/^\d+(\.\d+)?$/.test(value)) return 'N';
  return 'AN';
}

const SAFE_PATTERN_HINTS = new Set([
  'ADDRESS', 'CITY', 'COUNTY', 'CURRENCY', 'DATE', 'DOB', 'EMAIL', 'GENDER',
  'ID_NUMBER', 'INDICATOR', 'MEMBER_ID', 'NPI', 'PERSON_NAME', 'PHONE',
  'PROCEDURE', 'QUALIFIER', 'SSN', 'STATE', 'TAXONOMY', 'TIME', 'ZIP', 'NUM',
]);

function valuesForSafeFeatures(field: SourceField, rows: Record<string, string>[]): string[] {
  const rowValues = rows
    .filter(row => Object.prototype.hasOwnProperty.call(row, field.name))
    .map(row => String(row[field.name] ?? ''));
  if (rowValues.length > 0) return rowValues;
  if (Array.isArray(field.examples) && field.examples.length > 0) {
    return field.examples.map(value => String(value ?? ''));
  }
  return [String(field.example ?? '')];
}

function lengthBucketForLength(length: number): string {
  if (length <= 0) return 'empty';
  if (length === 1) return '1';
  if (length <= 4) return '2-4';
  if (length <= 8) return '5-8';
  if (length <= 12) return '9-12';
  if (length <= 20) return '13-20';
  if (length <= 40) return '21-40';
  return '41+';
}

function inferLengthBucket(values: string[]): string {
  const buckets = Array.from(new Set(
    values
      .map(value => String(value ?? '').trim().length)
      .filter(length => length > 0)
      .map(lengthBucketForLength),
  ));
  if (buckets.length === 0) return 'empty';
  if (buckets.length === 1) return buckets[0];
  return `mixed:${buckets.slice(0, 3).join('/')}`;
}

function inferBlankRate(values: string[]): number {
  if (values.length === 0) return 0;
  const blankCount = values.filter(value => String(value ?? '').trim() === '').length;
  return Math.round((blankCount / values.length) * 100) / 100;
}

function inferCardinalityHint(values: string[]): string {
  const nonBlank = values.map(value => String(value ?? '').trim()).filter(Boolean);
  if (nonBlank.length === 0) return 'empty';
  const distinct = new Set(nonBlank).size;
  if (distinct === 1) return 'constant';
  if (distinct === 2) return 'binary_enum';
  if (distinct <= 10 && distinct / nonBlank.length <= 0.5) return `low_enum_${distinct}`;
  if (distinct <= 10) return `low_cardinality_${distinct}`;
  if (distinct <= 50) return 'medium_cardinality';
  return 'high_cardinality';
}

function inferParentPath(name: string): string {
  const withoutIndexes = String(name ?? '').replace(/\[\d+\]/g, '[]');
  const dotParts = withoutIndexes.split('.').filter(Boolean);
  if (dotParts.length > 1) return dotParts.slice(0, -1).join('.').slice(0, 80);
  const slashParts = withoutIndexes.split('/').filter(Boolean);
  if (slashParts.length > 1) return slashParts.slice(0, -1).join('/').slice(0, 80);
  return '';
}

function inferPatternClasses(values: string[], fieldName: string, valueHint: string): string[] {
  const classes: string[] = [];
  const nameHint = inferValueHint('', fieldName);
  for (const hint of [valueHint, nameHint]) {
    if (hint && SAFE_PATTERN_HINTS.has(hint) && !classes.includes(hint)) classes.push(hint);
  }
  for (const value of values) {
    const trimmed = String(value ?? '').trim();
    if (!trimmed || /^\[REDACTED\]$/i.test(trimmed)) continue;
    const hint = inferValueHint(trimmed, '');
    if (hint && SAFE_PATTERN_HINTS.has(hint) && !classes.includes(hint)) {
      classes.push(hint);
      if (classes.length >= 6) break;
    }
  }
  return classes;
}

function buildSafeSourceFeatures(field: SourceField, rows: Record<string, string>[]): SafeSourceFeatures {
  const values = valuesForSafeFeatures(field, rows);
  const firstNonBlank = values.find(value => String(value ?? '').trim()) ?? '';
  const valueHint = field.valueHint || inferValueHint(firstNonBlank, field.name);
  return {
    valueHint,
    inferredDataType: field.dataType || inferDataType(firstNonBlank),
    lengthBucket: inferLengthBucket(values),
    blankRate: inferBlankRate(values),
    cardinalityHint: inferCardinalityHint(values),
    patternClasses: inferPatternClasses(values, field.name, valueHint),
    parentPath: inferParentPath(field.name),
  };
}

export function parseSourceFile(content: string, fileName: string, targetSpecType?: TargetSpecType, options?: ParseOptions): ParsedSourceData {
  const format = detectFormat(content, fileName);
  let result: ParsedSourceData;
  try {
    switch (format) {
      case 'JSON': result = parseJSON(content, options); break;
      case 'CSV': result = parseCSV(content, ',', options); break;
      case 'TSV': result = parseTSV(content, options); break;
      case 'Pipe-Delimited': result = parsePipeDelimited(content, targetSpecType, options); break;
      case 'EDI': result = parseEdi(content, options); break;
      case 'Fixed-Width/Text': result = parseFixedWidth(content, options); break;
      case 'XML': result = parseXML(content, options); break;
      default: result = parseGenericText(content, options);
    }
  } catch {
    result = parseGenericText(content, options);
  }

  for (const field of result.fields) {
    if (!field.dataType) {
      field.dataType = inferDataType(field.example);
    }
    if (!field.valueHint) {
      field.valueHint = inferValueHint(field.example, field.name);
    }
    field.sourceFeatures = buildSafeSourceFeatures(field, result.rows);
  }

  return result;
}

// ─── AI / LLM mapping logic ────────────────────────────────────────

/**
 * Build the full field list for the target HPS spec.
 */
function getTargetFields(specType: TargetSpecType) {
  const spec = getHpsSpec(specType);
  const targetFields: { record: string; fieldName: string; fieldNum: number; dataType: string; required: boolean; usage: 'Required' | 'Conditional' | 'Optional'; max?: number; values?: string[]; format?: string }[] = [];
  for (const [recCode, recSpec] of Object.entries(spec.records)) {
    for (const field of recSpec.fields) {
      targetFields.push({
        record: recCode,
        fieldName: field.name,
        fieldNum: field.num ?? 0,
        dataType: field.dataType,
        required: field.required,
        usage: field.usage,
        ...(field.max != null ? { max: field.max } : {}),
        ...(field.values && field.values.length > 0 ? { values: field.values } : {}),
        ...(field.format ? { format: field.format } : {}),
      });
    }
  }
  return { spec, targetFields };
}

function expectedValueForTargetField(targetField: { values?: string[]; format?: string }): string {
  if (targetField.values && targetField.values.length > 0) {
    return targetField.values.join(', ');
  }
  return targetField.format || '';
}

function buildUnmappedTargetField(tf: ReturnType<typeof getTargetFields>['targetFields'][number]) {
  const expectedValue = expectedValueForTargetField(tf);
  return {
    record: tf.record,
    field: tf.fieldName,
    fieldNum: tf.fieldNum,
    required: tf.required,
    usage: tf.usage,
    ...(expectedValue ? { expectedValue } : {}),
  };
}

function buildUnmappedSourceField(field: ParsedSourceData['fields'][number]): UnmappedSourceFieldEntry {
  return {
    field: field.name,
    example: field.example || '',
    ...(field.examples && field.examples.length > 0 ? { examples: field.examples } : {}),
    ...(field.dataType ? { dataType: field.dataType } : {}),
  };
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const REDACT_SOURCE_BEFORE_API = (process.env.REACT_APP_REDACT_SOURCE_BEFORE_API ?? 'true')
  .toLowerCase() !== 'false';

const CLIENT_PHI_FIELD_NAME_KEYWORDS = [
  'first name', 'last name', 'middle name', 'full name', 'patient name', 'member name', 'subscriber name',
  'dob', 'date of birth', 'birth date', 'age',
  'ssn', 'social security', 'ein', 'tax id', 'member id', 'subscriber id', 'mrn', 'medical record',
  'email', 'phone', 'fax',
  'address', 'street', 'city', 'county', 'zip', 'postal',
  'vin', 'license', 'certificate', 'device id', 'biometric', 'photo', 'image',
];

type DatePlaceholder = 'start' | 'end';

function replaceDateValue(date: string, placeholder: DatePlaceholder = 'start'): string {
  if (/^\d{8}$/.test(date)) return placeholder === 'end' ? '20901231' : '20900101';

  const match = date.match(/^(\d{1,2})([-/])(\d{1,2})([-/])(\d{2}|\d{4})$/);
  if (!match) return date;

  const first = placeholder === 'end' ? '12' : '1'.padStart(match[1].length, '0');
  const second = placeholder === 'end' ? '31' : '1'.padStart(match[3].length, '0');
  const year = match[5].length === 2 ? '90' : '2090';
  return `${first}${match[2]}${second}${match[4]}${year}`;
}

const CLIENT_PHI_REPLACEMENTS: Array<{
  pattern: RegExp;
  replacement: string | ((match: string, placeholder: DatePlaceholder) => string);
}> = [
  { pattern: /\b\d{3}-\d{2}-\d{4}\b/g, replacement: '***-**-****' },
  { pattern: /\b\d{2}-\d{7}\b/g, replacement: '**-*******' },
  { pattern: /\b(?:\d{4}[-\s]?){3}\d{4}\b/g, replacement: '****-****-****-****' },
  { pattern: /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g, replacement: '(***) ***-****' },
  { pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, replacement: '***@***.***' },
  { pattern: /https?:\/\/[^\s<>]+/gi, replacement: 'https://***.***' },
  { pattern: /\b(?:\d{1,3}\.){3}\d{1,3}\b/g, replacement: '***.***.***.***' },
  { pattern: /\b\d{1,5}\s+.{1,40}\b(?:St|Ave|Blvd|Dr|Ln|Rd|Ct|Way)\b\.?/gi, replacement: '[ADDRESS REDACTED]' },
  { pattern: /\b\d{1,5}\s+.{1,40}\b(?:Street|Avenue|Boulevard|Drive|Lane|Road|Court|Place|Circle|Parkway|Terrace|Highway)\b\.?/gi, replacement: '[ADDRESS REDACTED]' },
  { pattern: /\b(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\b/g, replacement: replaceDateValue },
  { pattern: /\b\d{1,2}[-/]\d{1,2}[-/](?:\d{2}|\d{4})\b/g, replacement: replaceDateValue },
  { pattern: /\b(?:zip|zipcode|zip\s*code|postal\s*code)\s*[:=#-]?\s*\d{5}(?:-\d{4})?\b/gi, replacement: 'ZIP: [ZIP REDACTED]' },
  { pattern: /\b(?:MRN|medical\s*record)\s*[-:#]?\s*\d{5,12}\b/gi, replacement: 'MRN: ********' },
  { pattern: /\bmember\s*(?:id|number)?\s*[:#=-]?\s*[A-Z0-9]{6,15}\b/gi, replacement: 'Member ID: ********' },
  { pattern: /\b(?:patient|member|subscriber|name)\s*[:=]\s*[A-Z][a-z]{1,20}\s+[A-Z][a-z]{1,20}\b/g, replacement: 'name: [NAME REDACTED]' },
];

function isClientPhiFieldName(name: string): boolean {
  const lower = (name || '').toLowerCase();
  return CLIENT_PHI_FIELD_NAME_KEYWORDS.some((keyword) => lower.includes(keyword));
}

function redactClientPhiText(value: string, placeholder: DatePlaceholder): string {
  if (!value) return value;
  let redacted = value;
  for (const { pattern, replacement } of CLIENT_PHI_REPLACEMENTS) {
    redacted = typeof replacement === 'string'
      ? redacted.replace(pattern, replacement)
      : redacted.replace(pattern, (match) => replacement(match, placeholder));
  }
  return redacted;
}

function redactSourceFieldsForApi(fields: SourceField[]): SourceField[] {
  return fields.map((sf) => {
    const examples = Array.isArray(sf.examples) && sf.examples.length > 0
      ? sf.examples
      : [sf.example ?? ''];
    const isPhiByName = isClientPhiFieldName(sf.name || '');
    const datePlaceholder: DatePlaceholder = /\b(end|termination|expiration|through)\b/i.test(sf.name || '')
      ? 'end'
      : 'start';

    const redactedExamples = examples.map((raw) => {
      const input = String(raw ?? '');
      const redacted = redactClientPhiText(input, datePlaceholder);
      if (isPhiByName && redacted === input && input.trim().length > 0) {
        return '[REDACTED]';
      }
      return redacted;
    });

    const primary = redactedExamples.find(Boolean) ?? '';
    return {
      ...sf,
      example: primary,
      examples: redactedExamples,
    };
  });
}

/**
 * Call the internal UHG LLM API (via services proxy) to produce the mapping.
 * Falls back to a local fuzzy-scoring heuristic when the server is unreachable.
 */
export async function generateMapping(
  sourceContent: string,
  fileName: string,
  targetSpecType: TargetSpecType,
  llmSettings?: LLMSettings,
): Promise<MappingResults> {
  const maxSampleRows = Math.min(Math.max(llmSettings?.max_lines ?? DEFAULT_MAX_SAMPLE_ROWS, 1), 500);
  const source = parseSourceFile(sourceContent, fileName, targetSpecType, { maxSampleRows });
  const { spec, targetFields } = getTargetFields(targetSpecType);

  // ── Attempt LLM call via server ─────────────────────────────────
  try {
    return await callServerLLM(source, targetFields, spec, targetSpecType, llmSettings);
  } catch {
    // Server mapping unavailable; fall back to local heuristic below.
  }

  // ── Fallback: local fuzzy heuristic ────────────────────────────
  return localFuzzyMapping(source, targetFields, spec, targetSpecType, 'Server mapping API was unavailable or returned an error.');
}

// ─── Server LLM call (UHG internal API via services) ────────────────

async function callServerLLM(
  source: ParsedSourceData,
  targetFields: ReturnType<typeof getTargetFields>['targetFields'],
  spec: ReturnType<typeof getHpsSpec>,
  targetSpecType: TargetSpecType,
  llmSettings?: LLMSettings,
): Promise<MappingResults> {
  const sourceFieldsForApi = REDACT_SOURCE_BEFORE_API
    ? redactSourceFieldsForApi(source.fields)
    : source.fields;

  const { accessToken } = getTokens();
  const response = await fetch(`${API_BASE_URL}/mapping`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({
      sourceFields: sourceFieldsForApi,
      sourceFormat: source.format,
      targetSpec: spec.name,
      targetVersion: spec.version,
      targetFields: targetFields.map(tf => ({
        record: tf.record,
        fieldName: tf.fieldName,
        fieldNum: tf.fieldNum,
        dataType: tf.dataType,
        required: tf.required,
        usage: tf.usage,
        ...(tf.max != null ? { max: tf.max } : {}),
        ...(tf.values && tf.values.length > 0 ? { values: tf.values } : {}),
        ...(tf.format ? { format: tf.format } : {}),
      })),
      ...(llmSettings ? { llmSettings } : {}),
    }),
  });

  if (!response.ok) {
    const errBody = await response.text();
    throw new Error(`Server mapping API error: ${response.status} - ${errBody}`);
  }

  const raw = await response.json();

  const targetFieldLookup = new Map(
    targetFields.map(tf => [`${tf.record}.${tf.fieldName}`, tf]),
  );
  const sourceFieldLookup = new Map(
    source.fields.map(sf => [sf.name, sf]),
  );
  const enrichedMappings: FieldMapping[] = (raw.mappings || []).map((m: FieldMapping) => {
    const targetFieldName = m.targetField.startsWith(`${m.targetRecord}.`)
      ? m.targetField.slice(m.targetRecord.length + 1)
      : m.targetField;
    const tf = targetFieldLookup.get(`${m.targetRecord}.${targetFieldName}`);
    const sourceField = sourceFieldLookup.get(m.sourceField);

    return {
      ...m,
      sourceExample: sourceField?.example ?? m.sourceExample,
      targetField: targetFieldName,
      targetFieldNum: tf?.fieldNum ?? m.targetFieldNum,
      dataType: tf?.dataType ?? m.dataType,
      usage: tf?.usage ?? (m.required ? 'Required' : 'Optional'),
      confidence: m.confidence,
      transformationNote: m.transformationNote ?? '',
    };
  });

  const mappedSourceNames = new Set(enrichedMappings.map(m => m.sourceField));
  const mappedTargetKeys = new Set(enrichedMappings.map(m => `${m.targetRecord}.${m.targetField}`));

  const unmappedSource = source.fields
    .filter(f => !mappedSourceNames.has(f.name))
    .map(buildUnmappedSourceField);

  const unmappedTarget = targetFields
    .filter(tf => !mappedTargetKeys.has(`${tf.record}.${tf.fieldName}`))
    .map(buildUnmappedTargetField);

  return {
    sourceFormat: source.format,
    targetSpec: spec.name,
    targetVersion: spec.version,
    summary: computeSummary(enrichedMappings, source.fields.length),
    mappings: enrichedMappings,
    unmappedSourceFields: unmappedSource,
    unmappedTargetFields: unmappedTarget,
    recommendations: raw.recommendations ?? [],
    warnings: raw.warnings ?? [],
    mappingSource: raw.mappingSource ?? 'server-llm',
    telemetry: raw.telemetry,
  };
}

// ─── Local fuzzy heuristic ─────────────────────────────────────────

function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Simple n-gram overlap score between 0 and 1 */
function ngramScore(a: string, b: string, n = 2): number {
  const na = normalize(a);
  const nb = normalize(b);
  if (!na || !nb) return 0;

  const gramsA = new Set<string>();
  for (let i = 0; i <= na.length - n; i++) gramsA.add(na.substring(i, i + n));
  const gramsB = new Set<string>();
  for (let i = 0; i <= nb.length - n; i++) gramsB.add(nb.substring(i, i + n));

  let overlap = 0;
  gramsA.forEach(g => { if (gramsB.has(g)) overlap++; });

  const total = Math.max(gramsA.size, gramsB.size);
  return total === 0 ? 0 : overlap / total;
}

/** Keyword-based boosting for common healthcare data aliases */
const KEYWORD_ALIASES: Record<string, string[]> = {
  'subscriber id': ['member id', 'subscriber', 'sub id', 'memberid', 'subscriberid', 'patient id', 'insured id'],
  'first name': ['fname', 'first', 'member first', 'patient first', 'given name'],
  'last name': ['lname', 'last', 'member last', 'patient last', 'surname', 'family name'],
  'date of birth': ['dob', 'birth date', 'birthdate', 'birth_date'],
  'group number': ['group id', 'groupid', 'group_number', 'grp number', 'grp id'],
  'creation date': ['file date', 'filedate', 'create date', 'created date', 'date created'],
  'trading partner id': ['partner id', 'tp id', 'tpid', 'trading_partner'],
  'claim number': ['claim id', 'claimid', 'claim_number', 'claim no'],
  'service date': ['dos', 'date of service', 'service_date', 'from date'],
  'diagnosis code': ['dx code', 'icd code', 'diag code', 'diagnosis'],
  'procedure code': ['cpt code', 'proc code', 'procedure', 'hcpcs code'],
  'billed amount': ['charge amount', 'total charge', 'billed', 'amount billed'],
  'paid amount': ['payment amount', 'amount paid', 'paid', 'payment'],
  'provider npi': ['npi', 'provider id', 'rendering npi', 'billing npi'],
  'submitter name': ['sender name', 'submitter', 'company name', 'org name'],
  'payer id': ['payer', 'insurance company', 'carrier id'],
  'record id': ['rec type', 'record type', 'row type'],
  'relationship code': ['relationship', 'relation', 'dependent code'],
  'gender': ['sex', 'gender code', 'member gender'],
  'address': ['street', 'address line', 'addr'],
  'city': ['city name'],
  'state': ['state code', 'st'],
  'zip': ['zip code', 'postal code', 'zipcode'],
};

function aliasBoostedScore(sourceName: string, targetName: string): number {
  const base = ngramScore(sourceName, targetName);
  const nSource = normalize(sourceName);
  const nTarget = normalize(targetName);

  // Exact or near-exact
  if (nSource === nTarget) return 1.0;

  // Check keyword aliases
  for (const [canonical, aliases] of Object.entries(KEYWORD_ALIASES)) {
    const canonNorm = normalize(canonical);
    const aliasNorms = aliases.map(normalize);
    const allVariants = [canonNorm, ...aliasNorms];

    const sourceMatchesAny = allVariants.some(v => nSource.includes(v) || v.includes(nSource));
    const targetMatchesAny = allVariants.some(v => nTarget.includes(v) || v.includes(nTarget));
    if (sourceMatchesAny && targetMatchesAny) {
      return Math.max(base, 0.85);
    }
  }

  return base;
}

function scoreToConfidence(score: number): MappingConfidence {
  if (score >= 0.7) return 'high';
  if (score >= 0.45) return 'medium';
  if (score >= 0.25) return 'low';
  return 'unmapped';
}

function buildFallbackNote(
  dataType: string,
  sourceExample: string,
  usage: 'Required' | 'Conditional' | 'Optional',
): string {
  const sourceEmpty = !sourceExample || sourceExample.trim() === '';

  if (sourceEmpty && usage === 'Required') {
    let note = 'Required field — source data is missing';
    if (dataType === 'DT' || dataType === 'D8') note += '. Expected format: CCYYMMDD';
    else if (dataType === 'TM' || dataType === 'T6') note += '. Expected format: HHMMSS';
    else if (dataType === 'N') note += '. Expected: numeric value';
    return note;
  }

  if (sourceEmpty && usage === 'Conditional') {
    let note = 'Conditional field — required when applicable, verify with payer';
    if (dataType === 'DT' || dataType === 'D8') note += '. Expected format: CCYYMMDD';
    else if (dataType === 'TM' || dataType === 'T6') note += '. Expected format: HHMMSS';
    else if (dataType === 'N') note += '. Expected: numeric value';
    return note;
  }

  if (sourceEmpty) {
    return '';
  }

  return checkDataTypeMismatch(sourceExample, dataType);
}


function checkDataTypeMismatch(sourceExample: string, targetDataType: string): string {
  if (!sourceExample?.trim()) return '';
  const val = sourceExample.trim();

  if ((targetDataType === 'DT' || targetDataType === 'D8') && !/^\d{8}$/.test(val)) {
    return `Source value "${val}" is not in CCYYMMDD date format`;
  }
  if ((targetDataType === 'TM' || targetDataType === 'T6') && !/^(?:[01]\d|2[0-3])[0-5]\d[0-5]\d$/.test(val)) {
    return `Source value "${val}" is not in HHMMSS time format`;
  }
  if (targetDataType === 'N' && !/^\d+(\.\d+)?$/.test(val)) {
    return `Source value "${val}" is not numeric — target expects numeric-only`;
  }
  return '';
}

function resolveTransform(
  sourceExample: string,
  dataType: string,
  usage: 'Required' | 'Conditional' | 'Optional',
  confidence: MappingConfidence,
): { transformNote: string; finalConfidence: MappingConfidence } {
  const sourceEmpty = !sourceExample?.trim();
  const typeWarning = checkDataTypeMismatch(sourceExample, dataType);

  let transformNote: string;
  if (sourceEmpty) {
    transformNote = usage === 'Optional' ? '' : buildFallbackNote(dataType, sourceExample, usage);
  } else if (!typeWarning && confidence === 'high') {
    transformNote = '';
  } else {
    transformNote = buildFallbackNote(dataType, sourceExample, usage);
  }

  const finalConfidence: MappingConfidence = (transformNote && confidence === 'high')
    ? 'medium'
    : confidence;

  return { transformNote, finalConfidence };
}

function buildLocalFallbackTelemetry(
  sourceFieldCount: number,
  targetFieldCount: number,
  mappedFieldCount: number,
  unmappedSourceFieldCount: number,
  unmappedTargetFieldCount: number,
  fallbackReason?: string,
) {
  return {
    batchCount: 0,
    fulfilledBatchCount: 0,
    failedBatchCount: 0,
    sourceFieldCount,
    targetFieldCount,
    llmCallCount: 0,
    latencyMs: 0,
    mappedFieldCount,
    unmappedSourceFieldCount,
    unmappedTargetFieldCount,
    warningCount: fallbackReason ? 1 : 0,
  };
}

function localFuzzyMapping(
  source: ParsedSourceData,
  targetFields: ReturnType<typeof getTargetFields>['targetFields'],
  spec: ReturnType<typeof getHpsSpec>,
  targetSpecType: TargetSpecType,
  fallbackReason?: string,
): MappingResults {
  const mappings: FieldMapping[] = [];
  const usedTargets = new Set<string>();
  const mappedSourceFields = new Set<string>();

  // For each source field, find best matching target field
  for (const srcField of source.fields) {
    let bestScore = 0;
    let bestTarget: (typeof targetFields)[0] | null = null;

    for (const tf of targetFields) {
      const key = `${tf.record}.${tf.fieldName}`;
      if (usedTargets.has(key)) continue;
      const srcName = srcField.name.includes('.') ? srcField.name.split('.').slice(1).join('.') : srcField.name;
      const score = aliasBoostedScore(srcName, tf.fieldName);
      if (score > bestScore) {
        bestScore = score;
        bestTarget = tf;
      }
    }

    const confidence = scoreToConfidence(bestScore);
    if (bestTarget && confidence !== 'unmapped') {
      const key = `${bestTarget.record}.${bestTarget.fieldName}`;
      usedTargets.add(key);
      mappedSourceFields.add(srcField.name);

      const { transformNote, finalConfidence } = resolveTransform(
        srcField.example, bestTarget.dataType, bestTarget.usage, confidence,
      );

      mappings.push({
        sourceField: srcField.name,
        sourceExample: srcField.example,
        targetRecord: bestTarget.record,
        targetField: bestTarget.fieldName,
        targetFieldNum: bestTarget.fieldNum,
        confidence: finalConfidence,
        transformationNote: transformNote,
        dataType: bestTarget.dataType,
        required: bestTarget.required,
        usage: bestTarget.usage,
      });
    }
  }

  const unmappedSourceFields = source.fields
    .filter(f => !mappedSourceFields.has(f.name))
    .map(buildUnmappedSourceField);

  const unmappedTargetFields = targetFields
    .filter(tf => !usedTargets.has(`${tf.record}.${tf.fieldName}`))
    .map(buildUnmappedTargetField);

  return {
    sourceFormat: source.format,
    targetSpec: spec.name,
    targetVersion: spec.version,
    summary: computeSummary(mappings, source.fields.length),
    mappings,
    unmappedSourceFields,
    unmappedTargetFields,
    recommendations: buildFuzzyRecommendations(mappings, unmappedSourceFields, unmappedTargetFields),
    warnings: fallbackReason ? [fallbackReason] : [],
    mappingSource: 'local-fallback',
    fallbackReason,
    telemetry: buildLocalFallbackTelemetry(source.fields.length, targetFields.length, mappings.length, unmappedSourceFields.length, unmappedTargetFields.length, fallbackReason),
  };
}

function buildFuzzyRecommendations(
  mappings: FieldMapping[],
  unmappedSourceFields: UnmappedSourceFieldEntry[],
  unmappedTargetFields: { record: string; field: string; required: boolean; fieldNum?: number; expectedValue?: string }[],
): string[] {
  const recommendations: string[] = [];
  const missingRequired = unmappedTargetFields.filter(f => f.required);
  if (missingRequired.length > 0) {
    const missingRequiredFields = missingRequired.map(f => `${f.record}.${f.field}`).join(', ');
    recommendations.push(
      `⚠️ ${missingRequired.length} required target field(s) are unmapped: ${missingRequiredFields}. You must provide values for these fields.`,
    );
  }
  if (unmappedSourceFields.length > 0) {
    recommendations.push(
      `ℹ️ ${unmappedSourceFields.length} source field(s) could not be auto-mapped. Review them manually for any relevant data.`,
    );
  }
  const lowConfCount = mappings.filter(m => m.confidence === 'low').length;
  if (lowConfCount > 0) {
    recommendations.push(
      `🔍 ${lowConfCount} mapping(s) have low confidence. Consider manually verifying these before generating the output file.`,
    );
  }
  recommendations.push(
    '💡 AI-powered mapping via the internal UHG LLM gateway is used automatically when the services server is running (`npm run server`).',
  );
  return recommendations;
}

function computeSummary(mappings: FieldMapping[], totalSource: number) {
  const mapped = mappings.length;
  return {
    totalSourceFields: totalSource,
    mappedFields: mapped,
    unmappedFields: totalSource - mapped,
  };
}
